import React from "react";
import { platformStats } from "../../../data/mockData";
import Card from "../../../components/ui/Card";
import DateRangeFilter from "../components/DateRangeFilter";
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  Briefcase,
  Layers,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";

const GrowthChart = ({ data, title, keyProp, labelProp, color = "bg-primary" }) => {
  const maxValue = Math.max(...data.map((item) => item[keyProp]));
  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 h-full flex flex-col">
      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
        <BarChart3 size={20} className="text-primary" />
        {title}
      </h3>
      <div className="flex-grow mt-6 flex justify-between items-end space-x-2 h-48">
        {data.map((item, index) => (
          <div
            key={index}
            className="flex-1 flex flex-col items-center justify-end group h-full"
          >
            <div className="relative w-full flex flex-col items-center justify-end group cursor-pointer">
              <div className="absolute -top-8 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                {item[keyProp].toLocaleString()}
              </div>
              <div
                className={`w-4/5 ${color} rounded-t-lg group-hover:brightness-110 transition-all shadow-sm`}
                style={{ height: `${(item[keyProp] / maxValue) * 100}%` }}
              ></div>
            </div>
            <span className="text-[10px] font-bold text-slate-400 mt-3">{item[labelProp]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminAnalyticsPage = () => {
  const totalRevenue = platformStats.revenueBreakdown.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Platform Analytics</h1>
          <p className="text-slate-500 font-medium mt-1">Deep dive into platform growth and revenue metrics.</p>
        </div>
        <DateRangeFilter />
      </div>

      {/* Metric Highligts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: "Rev. Growth", value: "+12.4%", icon: DollarSign, trend: "up" },
          { label: "User Retention", value: "64.2%", icon: Users, trend: "up" },
          { label: "Creator Yield", value: "2,450 ETB", icon: Briefcase, trend: "up" },
          { label: "Conversion Rate", value: "3.8%", icon: TrendingUp, trend: "down" },
        ].map((item, i) => (
          <Card key={i} className="flex flex-col p-5">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2.5 bg-slate-50 text-slate-600 rounded-xl">
                <item.icon size={20} />
              </div>
              <div className={`flex items-center text-[10px] font-black px-2 py-1 rounded-full ${
                item.trend === 'up' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
              }`}>
                {item.trend === 'up' ? <ArrowUpRight size={12} className="mr-0.5" /> : <ArrowDownRight size={12} className="mr-0.5" />}
                {item.trend === 'up' ? 'Increase' : 'Decrease'}
              </div>
            </div>
            <p className="text-2xl font-black text-slate-900 leading-tight">{item.value}</p>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{item.label}</p>
          </Card>
        ))}
      </div>

      {/* Main Charts area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
         <GrowthChart 
            data={platformStats.transactionVolume}
            title="Revenue Velocity (ETB)"
            keyProp="count"
            labelProp="date"
            color="bg-emerald-500"
         />
         <GrowthChart 
            data={platformStats.monthlySignups}
            title="Acquisition Curve"
            keyProp="count"
            labelProp="month"
            color="bg-primary"
         />
      </div>

      {/* Revenue Breakdown & More */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-2">
            <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                    <Layers size={20} className="text-indigo-600" />
                    Revenue Stream Analysis
                </h3>
                <span className="text-2xl font-black">{totalRevenue.toLocaleString()} <span className="text-xs text-slate-400">ETB</span></span>
            </div>
            <div className="space-y-8">
                {platformStats.revenueBreakdown.map((item) => (
                <div key={item.source}>
                    <div className="flex justify-between text-sm mb-3 font-black">
                        <span className="text-slate-600 flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${item.color.replace('bg-', 'bg-')}`}></span>
                            {item.source}
                        </span>
                        <span className="text-slate-900">{((item.amount / totalRevenue) * 100).toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-slate-50 rounded-full h-4 relative">
                        <div
                            className={`${item.color} h-4 rounded-full shadow-lg shadow-indigo-100 transition-all duration-1000`}
                            style={{ width: `${(item.amount / totalRevenue) * 100}%` }}
                        ></div>
                    </div>
                    <div className="flex justify-between mt-2">
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Gross Revenue</span>
                        <span className="text-xs font-black text-slate-700">{item.amount.toLocaleString()} ETB</span>
                    </div>
                </div>
                ))}
            </div>
        </Card>

        <Card className="bg-slate-900 text-white border-none shadow-2xl">
            <h3 className="text-lg font-black mb-6 uppercase tracking-widest text-white/50 border-b border-white/10 pb-4">Market Segmentation</h3>
            <div className="space-y-6">
                {[
                   { name: "Addis Ababa", val: "68%", color: "bg-primary" },
                   { name: "Regional Hubs", val: "22%", color: "bg-blue-500" },
                   { name: "International", val: "10%", color: "bg-purple-500" }
                ].map((market, i) => (
                    <div key={i} className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-2xl ${market.color} flex-shrink-0 flex items-center justify-center font-black text-xs`}>
                            {market.val}
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-bold">{market.name}</p>
                            <div className="h-1 w-full bg-white/10 rounded-full mt-2">
                                <div className={`h-full ${market.color} rounded-full`} style={{width: market.val}}></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <button className="w-full mt-10 py-4 bg-white/10 hover:bg-white/20 transition-all rounded-2xl font-black text-[10px] uppercase tracking-[0.2em]">
                Regional Export PDF
            </button>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
